<script setup>
import { router } from "@inertiajs/vue3";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import { useModalogStore } from "@/stores/modalog.js";

/**
 * "View Confirmed Gifts?" / "Hide Confirmed Gifts" buttons for the list
 * detail pages (GH-853, per Figma). Viewing routes through the spoiler
 * warning modalog; hiding links straight back to the plain list view.
 */
const props = defineProps({
    list: {
        type: Object,
        required: true,
    },
    variant: {
        type: String,
        default: "view", // "view" | "hide"
    },
});

const modalogStore = useModalogStore();

function openSpoilerModalog() {
    modalogStore.showModalog({
        type: "ModalogConfirmedGiftsSpoiler",
        props: { list: props.list },
    });
}

// BrandButton renders a plain <a> when given an href, which would skip
// Inertia — so hiding navigates from a click handler instead.
function hideConfirmedGifts() {
    router.get(route("list.show", { giftlist: props.list.uuid }));
}
</script>

<template>
    <BrandButton
        v-if="variant === 'hide'"
        variant="whiteOnOrange"
        class="h-[52px] px-6 text-[15px]"
        @click="hideConfirmedGifts"
    >
        <template #icon>
            <svg
                viewBox="0 0 24 16"
                class="h-3.5 w-[21px]"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                aria-hidden="true"
            >
                <path
                    d="M2 8c2.7-4 6-6 10-6s7.3 2 10 6c-2.7 4-6 6-10 6S4.7 12 2 8z"
                />
                <circle
                    cx="12"
                    cy="8"
                    r="2.4"
                    fill="currentColor"
                    stroke="none"
                />
                <path d="M4 15L20 1" />
            </svg>
        </template>
        Hide Confirmed Gifts
    </BrandButton>
    <BrandButton
        v-else
        icon-rest-class="text-brand-red"
        class="h-[52px] px-6 text-[15px]"
        @click="openSpoilerModalog"
    >
        <template #icon>
            <!-- Red gift box, from the Figma button asset. Even height keeps
                 the centering offset integral so the glyph doesn't jiggle on
                 the hover lift. -->
            <svg
                viewBox="0 0 21.12 20.16"
                class="h-[18px] w-[19px]"
                fill="currentColor"
                aria-hidden="true"
            >
                <path
                    d="M2.112 18.24V9.60001C1.5312 9.60001 1.03418 9.41217 0.620928 9.03649C0.20768 8.66081 0.000704249 8.20865 2.49594e-07 7.68001V5.76001C2.49594e-07 5.23201 0.206976 4.78017 0.620928 4.40449C1.03488 4.02881 1.5319 3.84065 2.112 3.84001H5.4912C5.4032 3.69601 5.34617 3.54401 5.32013 3.38401C5.29408 3.22401 5.2807 3.05601 5.28 2.88001C5.28 2.08001 5.588 1.40001 6.204 0.840009C6.82 0.280009 7.568 9.17912e-06 8.448 9.17912e-06C8.8528 9.17912e-06 9.2312 0.0681692 9.5832 0.204489C9.9352 0.340809 10.2608 0.528649 10.56 0.768009C10.8592 0.512009 11.1848 0.320009 11.5368 0.192009C11.8888 0.0640092 12.2672 9.17912e-06 12.672 9.17912e-06C13.552 9.17912e-06 14.3 0.280009 14.916 0.840009C15.532 1.40001 15.84 2.08001 15.84 2.88001C15.84 3.05601 15.8224 3.22017 15.7872 3.37249C15.752 3.52481 15.6992 3.68065 15.6288 3.84001H19.008C19.5888 3.84001 20.0862 4.02817 20.5001 4.40449C20.9141 4.78081 21.1207 5.23265 21.12 5.76001V7.68001C21.12 8.20801 20.9134 8.66017 20.5001 9.03649C20.0869 9.41281 19.5895 9.60065 19.008 9.60001V18.24C19.008 18.768 18.8014 19.2202 18.3881 19.5965C17.9749 19.9728 17.4775 20.1606 16.896 20.16H4.224C3.6432 20.16 3.14617 19.9722 2.73293 19.5965C2.31968 19.2208 2.1127 18.7686 2.112 18.24ZM11.9201 2.19553C11.7174 2.37985 11.616 2.60801 11.616 2.88001C11.616 3.15201 11.7174 3.38017 11.9201 3.56449C12.1229 3.74881 12.3735 3.84065 12.672 3.84001C12.9705 3.83937 13.2215 3.74721 13.4249 3.56353C13.6284 3.37985 13.7294 3.15201 13.728 2.88001C13.7266 2.60801 13.6252 2.38017 13.4239 2.19649C13.2225 2.01281 12.9719 1.92065 12.672 1.92001C12.3721 1.91937 12.1215 2.01153 11.9201 2.19649M7.392 2.88001C7.392 3.15201 7.49337 3.38017 7.69613 3.56449C7.89888 3.74881 8.1495 3.84065 8.448 3.84001C8.74649 3.83937 8.99747 3.74721 9.20093 3.56353C9.40438 3.37985 9.5054 3.15201 9.504 2.88001C9.50259 2.60801 9.40121 2.38017 9.19987 2.19649C8.99853 2.01281 8.7479 1.92065 8.448 1.92001C8.14809 1.91937 7.89747 2.01153 7.69613 2.19649C7.49478 2.38145 7.39341 2.60929 7.392 2.88001ZM2.112 5.76001V7.68001H9.504V5.76001H2.112ZM9.504 18.24V9.60001H4.224V18.24H9.504ZM11.616 18.24H16.896V9.60001H11.616V18.24ZM19.008 7.68001V5.76001H11.616V7.68001H19.008Z"
                />
            </svg>
        </template>
        View Confirmed Gifts?
    </BrandButton>
</template>
